/**
 * GA4 adapter for typed analytics events
 * Maps provider-agnostic AnalyticsEvent to GA4 format
 */

import type { AnalyticsEvent } from '../../../types/analytics-events';

// Declare window.gtag for GA4
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    gtag_ready?: boolean;
  }
}

/**
 * Map AnalyticsEvent to GA4 event name
 */
function mapEventNameToGA4(event: AnalyticsEvent): string {
  // Most events use their name directly, but some need mapping
  const mapping: Record<string, string> = {
    'search_query': 'search',
    'search_pagination': 'page_view', // GA4 uses page_view for pagination
    'company_link_click': 'click',
  };
  
  return mapping[event.name] || event.name;
}

/**
 * Map AnalyticsEvent params to GA4 parameters
 */
function mapParamsToGA4(event: AnalyticsEvent): Record<string, any> {
  const baseParams: Record<string, any> = {};
  
  switch (event.name) {
    case 'job_view':
      baseParams.job_id = event.params.job_id;
      baseParams.job_title = event.params.job_title;
      baseParams.company_id = event.params.company_id;
      if (event.params.company_name) baseParams.company_name = event.params.company_name;
      if (event.params.location_label) baseParams.location_label = event.params.location_label;
      if (event.params.is_remote !== undefined) baseParams.is_remote = event.params.is_remote;
      if (event.params.has_salary_insight !== undefined) baseParams.has_salary_insight = event.params.has_salary_insight;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'job';
      baseParams.event_label = event.params.job_title;
      break;
      
    case 'company_view':
      baseParams.company_id = event.params.company_id;
      if (event.params.company_name) baseParams.company_name = event.params.company_name;
      if (event.params.industry) baseParams.industry = event.params.industry;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'company';
      baseParams.event_label = event.params.company_name || event.params.company_id;
      break;
      
    case 'srp_view':
      baseParams.srp_id = event.params.srp_id;
      if (event.params.query_text) baseParams.query_text = event.params.query_text;
      if (event.params.location_label) baseParams.location_label = event.params.location_label;
      if (event.params.result_count !== undefined) baseParams.result_count = event.params.result_count;
      if (event.params.filters_applied) baseParams.filters_applied = event.params.filters_applied;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'search';
      baseParams.event_label = event.params.query_text || 'srp_view';
      break;
      
    case 'job_apply_click':
      baseParams.job_id = event.params.job_id;
      baseParams.company_id = event.params.company_id;
      if (event.params.apply_destination) baseParams.apply_destination = event.params.apply_destination;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'conversion';
      baseParams.event_label = 'job_apply';
      break;
      
    case 'salary_insight_view':
      baseParams.job_id = event.params.job_id;
      baseParams.company_id = event.params.company_id;
      if (event.params.has_exact_salary !== undefined) baseParams.has_exact_salary = event.params.has_exact_salary;
      if (event.params.has_estimated_salary !== undefined) baseParams.has_estimated_salary = event.params.has_estimated_salary;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'job';
      baseParams.event_label = 'salary_insight_view';
      break;
      
    case 'salary_insight_interaction':
      baseParams.job_id = event.params.job_id;
      baseParams.company_id = event.params.company_id;
      baseParams.interaction_type = event.params.interaction_type;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'job';
      baseParams.event_label = event.params.interaction_type;
      break;
      
    case 'job_click':
      baseParams.job_id = event.params.job_id;
      baseParams.job_title = event.params.job_title;
      baseParams.company_id = event.params.company_id;
      if (event.params.position !== undefined) baseParams.position = event.params.position;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'job';
      baseParams.event_label = event.params.job_title;
      baseParams.value = event.params.position;
      break;
      
    case 'job_save':
    case 'job_unsave':
    case 'job_share':
      baseParams.job_id = event.params.job_id;
      baseParams.job_title = event.params.job_title;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'job';
      baseParams.event_label = event.params.job_title;
      break;
      
    case 'filter_add':
    case 'filter_remove':
      baseParams.filter_key = event.params.filter_key;
      baseParams.filter_value = event.params.filter_value;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'search';
      baseParams.event_label = `${event.params.filter_key}: ${event.params.filter_value}`;
      break;
      
    case 'sort_change':
      baseParams.sort_type = event.params.sort_type;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'search';
      baseParams.event_label = event.params.sort_type;
      break;
      
    case 'search_query':
      baseParams.search_term = event.params.query;
      if (event.params.hits_count !== undefined) baseParams.hits_count = event.params.hits_count;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'search';
      baseParams.event_label = event.params.query;
      break;
      
    case 'search_pagination':
      baseParams.page_number = event.params.page_number;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'navigation';
      baseParams.event_label = `Page ${event.params.page_number}`;
      break;
      
    case 'search_suggestion_click':
      baseParams.search_term = event.params.suggestion;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'search';
      baseParams.event_label = event.params.suggestion;
      break;
      
    case 'company_link_click':
      baseParams.company_id = event.params.company_id;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'interaction';
      baseParams.event_label = event.params.company_id;
      break;
      
    case 'page_view':
      if (event.params.page_type) baseParams.page_type = event.params.page_type;
      if (event.params.job_id) baseParams.job_id = event.params.job_id;
      if (event.params.company_id) baseParams.company_id = event.params.company_id;
      if (event.params.srp_id) baseParams.srp_id = event.params.srp_id;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'navigation';
      break;
      
    case 'error_occurred':
      baseParams.error_message = event.params.error_message;
      if (event.params.error_context) baseParams.error_context = event.params.error_context;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'error';
      baseParams.event_label = event.params.error_message;
      break;
      
    case 'web_vital':
      baseParams.metric_name = event.params.metric_name;
      baseParams.value = event.params.value;
      if (event.params.locale) baseParams.locale = event.params.locale;
      baseParams.event_category = 'performance';
      baseParams.event_label = event.params.metric_name;
      break;
  }
  
  return baseParams;
}

/**
 * Send an AnalyticsEvent to GA4
 * Reuses existing window.gtag mechanism
 */
export function sendGA4Event(event: AnalyticsEvent): boolean {
  // Check browser availability
  if (typeof window === 'undefined') {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[GA4 Adapter] Cannot send event in non-browser environment:', event.name);
    }
    return false;
  }
  
  // Check GA4 readiness
  if (!window.gtag) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[GA4 Adapter] window.gtag not available. Event:', event.name);
    }
    return false;
  }
  
  try {
    const ga4EventName = mapEventNameToGA4(event);
    const ga4Params = mapParamsToGA4(event);
    
    // Development logging
    if (process.env.NODE_ENV === 'development') {
      console.log('[GA4 Adapter] Sending event:', {
        eventName: ga4EventName,
        params: ga4Params,
        originalEvent: event.name,
      });
    }
    
    // Send to GA4 using existing gtag mechanism
    window.gtag('event', ga4EventName, ga4Params);
    
    return true;
  } catch (error) {
    console.error('[GA4 Adapter] Error sending event:', error, event);
    return false;
  }
}

